import React, { useState, useEffect } from 'react';
import { EyeIcon, CheckIcon, XMarkIcon, PencilIcon, ClockIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import toast from 'react-hot-toast';
import apiService from '../../../services/api';
import { UserRole } from '../../types/admin.types';

interface User {
    id: number;
    firstName: string;
    lastName: string;
    middleName?: string;
    phoneNumber: string;
    email?: string;
    iin?: string;
    position?: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    rejectionReason?: string;
    company?: {
        id: number;
        bin: string;
        name: string;
        legalAddress: string;
        actualAddress: string;
        contactPhone: string;
        contactEmail: string;
        directorName: string;
    };
}

interface Document {
    id: number;
    type: string;
    typeDescription: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    status: string;
    uploadedAt: string;
    rejectionReason?: string;
    fileUrl?: string;
}

interface ModerationQueueProps {
    userRole: UserRole;
}

export const ModerationQueueNew: React.FC<ModerationQueueProps> = ({ userRole }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedUserDocuments, setSelectedUserDocuments] = useState<Document[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [actionType, setActionType] = useState<'approve' | 'reject' | 'request-changes' | null>(null);
    const [actionComment, setActionComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // Filters
    const [statusFilter, setStatusFilter] = useState('PENDING_MODERATION');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDir, setSortDir] = useState('desc');

    useEffect(() => {
        loadModerationRequests();
    }, [currentPage, statusFilter, sortBy, sortDir]);

    const loadModerationRequests = async () => {
        setIsLoading(true);
        try {
            const response = await apiService.getModerationRequests({
                page: currentPage,
                size: 20,
                status: statusFilter,
                sortBy,
                sortDir
            });

            if (response.data.success) {
                const pageData = response.data.data;
                setUsers(pageData.content || []);
                setTotalPages(pageData.totalPages || 0);
                setTotalElements(pageData.totalElements || 0);
            } else {
                toast.error(response.data.message || 'Ошибка загрузки заявок');
            }
        } catch (error: any) {
            console.error('Ошибка загрузки заявок:', error);
            toast.error('Ошибка загрузки заявок');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewDetails = async (user: User) => {
        try {
            const response = await apiService.getModerationRequestDetails(user.id);
            if (response.data.success) {
                const details = response.data.data;
                setSelectedUser(details);
                setSelectedUserDocuments(details.documents || []);
                setIsModalOpen(true);
            } else {
                toast.error(response.data.message || 'Ошибка загрузки деталей');
            }
        } catch (error: any) {
            console.error('Ошибка загрузки деталей:', error);
            toast.error('Ошибка загрузки деталей заявки');
        }
    };

    const handleAction = (type: 'approve' | 'reject' | 'request-changes') => {
        setActionType(type);
        setActionComment('');
    };

    const submitAction = async () => {
        if (!selectedUser || !actionType) return;

        if ((actionType === 'reject' || actionType === 'request-changes') && !actionComment.trim()) {
            toast.error('Введите комментарий');
            return;
        }

        setIsSubmitting(true);
        try {
            let response;
            switch (actionType) {
                case 'approve':
                    response = await apiService.approveApplication(selectedUser.id, actionComment);
                    break;
                case 'reject':
                    response = await apiService.rejectApplication(selectedUser.id, actionComment);
                    break;
                case 'request-changes':
                    response = await apiService.requestChanges(selectedUser.id, actionComment);
                    break;
            }

            if (response.data.success) {
                toast.success(response.data.message);
                setIsModalOpen(false);
                setActionType(null);
                setActionComment('');
                loadModerationRequests();
            } else {
                toast.error(response.data.message);
            }
        } catch (error: any) {
            console.error('Ошибка выполнения действия:', error);
            toast.error('Ошибка выполнения действия');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            'PENDING_MODERATION': { color: 'bg-yellow-100 text-yellow-800', text: 'На модерации' },
            'APPROVED': { color: 'bg-green-100 text-green-800', text: 'Одобрено' },
            'REJECTED': { color: 'bg-red-100 text-red-800', text: 'Отклонено' },
            'NEEDS_REVISION': { color: 'bg-orange-100 text-orange-800', text: 'На доработке' },
            'DRAFT': { color: 'bg-gray-100 text-gray-800', text: 'Черновик' }
        };

        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.DRAFT;
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                {config.text}
            </span>
        );
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleViewDocument = (document: Document) => {
        if (document.fileUrl) {
            window.open(document.fileUrl, '_blank');
        } else {
            toast.error('URL документа недоступен');
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow border">
                <div className="flex flex-wrap gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="input-field w-48"
                        >
                            <option value="PENDING_MODERATION">На модерации</option>
                            <option value="APPROVED">Одобрено</option>
                            <option value="REJECTED">Отклонено</option>
                            <option value="NEEDS_REVISION">На доработке</option>
                            <option value="DRAFT">Черновик</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Сортировка</label>
                        <select
                            value={`${sortBy}-${sortDir}`}
                            onChange={(e) => {
                                const [field, direction] = e.target.value.split('-');
                                setSortBy(field);
                                setSortDir(direction);
                            }}
                            className="input-field w-48"
                        >
                            <option value="createdAt-desc">Дата создания ↓</option>
                            <option value="createdAt-asc">Дата создания ↑</option>
                            <option value="updatedAt-desc">Дата изменения ↓</option>
                            <option value="updatedAt-asc">Дата изменения ↑</option>
                            <option value="lastName-asc">Фамилия А-Я</option>
                            <option value="lastName-desc">Фамилия Я-А</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={loadModerationRequests}
                            className="btn-secondary"
                            disabled={isLoading}
                        >
                            Обновить
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Заявитель
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Компания
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Статус
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Дата подачи
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Действия
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => (
                            <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {user.lastName} {user.firstName} {user.middleName}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {user.phoneNumber}
                                                {user.email && ` • ${user.email}`}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {user.company?.name || 'Не указана'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        БИН: {user.company?.bin || 'Не указан'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getStatusBadge(user.status)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {new Date(user.createdAt).toLocaleString('ru-RU')}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                        onClick={() => handleViewDetails(user)}
                                        className="text-blue-600 hover:text-blue-900 mr-3"
                                        title="Просмотреть детали"
                                    >
                                        <EyeIcon className="h-5 w-5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {users.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        Заявки не найдены
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                        Показано {currentPage * 20 + 1}-{Math.min((currentPage + 1) * 20, totalElements)} из {totalElements}
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 0}
                            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                            Назад
                        </button>
                        <span className="text-sm text-gray-700">
                            Страница {currentPage + 1} из {totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage >= totalPages - 1}
                            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Далее
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && selectedUser && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Детали заявки: {selectedUser.lastName} {selectedUser.firstName}
                                </h3>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* User Info */}
                                <div className="space-y-4">
                                    <h4 className="font-medium text-gray-900">Информация о заявителе</h4>
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                        <p><strong>ФИО:</strong> {selectedUser.lastName} {selectedUser.firstName} {selectedUser.middleName}</p>
                                        <p><strong>Телефон:</strong> {selectedUser.phoneNumber}</p>
                                        {selectedUser.email && <p><strong>Email:</strong> {selectedUser.email}</p>}
                                        {selectedUser.iin && <p><strong>ИИН:</strong> {selectedUser.iin}</p>}
                                        {selectedUser.position && <p><strong>Должность:</strong> {selectedUser.position}</p>}
                                        <p><strong>Статус:</strong> {getStatusBadge(selectedUser.status)}</p>
                                        <p><strong>Дата подачи:</strong> {new Date(selectedUser.createdAt).toLocaleString('ru-RU')}</p>
                                    </div>

                                    {selectedUser.company && (
                                        <>
                                            <h4 className="font-medium text-gray-900">Информация о компании</h4>
                                            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                                <p><strong>Название:</strong> {selectedUser.company.name}</p>
                                                <p><strong>БИН:</strong> {selectedUser.company.bin}</p>
                                                <p><strong>Юридический адрес:</strong> {selectedUser.company.legalAddress}</p>
                                                <p><strong>Фактический адрес:</strong> {selectedUser.company.actualAddress}</p>
                                                <p><strong>Телефон:</strong> {selectedUser.company.contactPhone}</p>
                                                <p><strong>Email:</strong> {selectedUser.company.contactEmail}</p>
                                                <p><strong>Директор:</strong> {selectedUser.company.directorName}</p>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Documents */}
                                <div className="space-y-4">
                                    <h4 className="font-medium text-gray-900">Документы ({selectedUserDocuments.length})</h4>
                                    <div className="space-y-3 max-h-96 overflow-y-auto">
                                        {selectedUserDocuments.map((doc) => (
                                            <div key={doc.id} className="bg-gray-50 p-4 rounded-lg">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h5 className="font-medium text-sm">{doc.typeDescription}</h5>
                                                    {getStatusBadge(doc.status)}
                                                </div>
                                                <p className="text-sm text-gray-600 mb-1">{doc.fileName}</p>
                                                <p className="text-xs text-gray-500 mb-2">
                                                    {formatFileSize(doc.fileSize)} • {new Date(doc.uploadedAt).toLocaleString('ru-RU')}
                                                </p>
                                                {doc.rejectionReason && (
                                                    <div className="bg-red-50 border border-red-200 rounded p-2 mb-2">
                                                        <p className="text-sm text-red-700">
                                                            <strong>Причина отклонения:</strong> {doc.rejectionReason}
                                                        </p>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => handleViewDocument(doc)}
                                                    className="btn-secondary text-xs"
                                                >
                                                    <EyeIcon className="h-4 w-4 mr-1" />
                                                    Просмотреть
                                                </button>
                                            </div>
                                        ))}
                                        {selectedUserDocuments.length === 0 && (
                                            <p className="text-gray-500 text-center py-4">Документы не загружены</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {selectedUser.rejectionReason && (
                                <div className="mt-4 bg-red-50 border border-red-200 rounded p-4">
                                    <h4 className="font-medium text-red-900 mb-2">Причина отклонения/комментарий:</h4>
                                    <p className="text-red-700">{selectedUser.rejectionReason}</p>
                                </div>
                            )}

                            {/* Action Section */}
                            {selectedUser.status === 'PENDING_MODERATION' && (
                                <div className="mt-6 border-t pt-4">
                                    {!actionType ? (
                                        <div className="flex space-x-3">
                                            <button
                                                onClick={() => handleAction('approve')}
                                                className="btn-primary flex items-center"
                                            >
                                                <CheckIcon className="h-4 w-4 mr-2" />
                                                Одобрить
                                            </button>
                                            <button
                                                onClick={() => handleAction('request-changes')}
                                                className="btn-secondary flex items-center"
                                            >
                                                <PencilIcon className="h-4 w-4 mr-2" />
                                                На доработку
                                            </button>
                                            <button
                                                onClick={() => handleAction('reject')}
                                                className="btn-danger flex items-center"
                                            >
                                                <XMarkIcon className="h-4 w-4 mr-2" />
                                                Отклонить
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <h4 className="font-medium text-gray-900">
                                                {actionType === 'approve' && 'Одобрение заявки'}
                                                {actionType === 'reject' && 'Отклонение заявки'}
                                                {actionType === 'request-changes' && 'Отправка на доработку'}
                                            </h4>

                                            {(actionType === 'reject' || actionType === 'request-changes') && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {actionType === 'reject' ? 'Причина отклонения *' : 'Комментарий к доработке *'}
                                                    </label>
                                                    <textarea
                                                        value={actionComment}
                                                        onChange={(e) => setActionComment(e.target.value)}
                                                        rows={4}
                                                        className="input-field w-full"
                                                        placeholder={actionType === 'reject' ? 'Укажите причину отклонения...' : 'Укажите что нужно исправить...'}
                                                    />
                                                </div>
                                            )}

                                            {actionType === 'approve' && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Комментарий (необязательно)
                                                    </label>
                                                    <textarea
                                                        value={actionComment}
                                                        onChange={(e) => setActionComment(e.target.value)}
                                                        rows={3}
                                                        className="input-field w-full"
                                                        placeholder="Дополнительный комментарий..."
                                                    />
                                                </div>
                                            )}

                                            <div className="flex space-x-3">
                                                <button
                                                    onClick={submitAction}
                                                    disabled={isSubmitting}
                                                    className={`${actionType === 'approve' ? 'btn-primary' :
                                                        actionType === 'reject' ? 'btn-danger' : 'btn-secondary'
                                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                >
                                                    {isSubmitting ? 'Выполняется...' : 'Подтвердить'}
                                                </button>
                                                <button
                                                    onClick={() => setActionType(null)}
                                                    disabled={isSubmitting}
                                                    className="btn-secondary"
                                                >
                                                    Отмена
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
